import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import useStore, { Filters } from "store";
import Slider from "rc-slider";

import styled from "@emotion/styled";

import Button from "components/Button";
import Text from "components/Text";

import messages from "../../store/messages";

type Props = {
  setOpened: (bool:boolean) => void;
}

function MobileFilters({ setOpened }: Props) {
  const { palette } = useTheme();
  const { filters, setFilters, currencySymbol } = useStore();
  
  const defaultRangePrices = useMemo(() => [], []);
  
  const defaultFilters = useMemo(() => ({
    priceRangeFilter: defaultRangePrices,
    carTypesFilter: [],
    fuelTypesFilter: [],
    gearboxFilter: [],
  }), [defaultRangePrices]);

  const [filtersSelected, setFiltersSelected] = useState<Filters>(filters || defaultFilters);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked, parentElement } = e.target;

    const whatToChange = parentElement?.parentElement?.id as keyof Filters;
    const value = id.toLowerCase();

    if (checked) {
      setFiltersSelected((prev) => ({ ...prev, [whatToChange]: prev[whatToChange]?.concat(value) }));
    } else {
      setFiltersSelected((prev) => ({ ...prev, [whatToChange]: prev[whatToChange]?.filter((item) => item !== value) }));
    }
  }, []);

  const onClose = useCallback(
    () => {
      document.body.classList.remove("modal-open");
      setOpened(false);
    },
    [setOpened],
  );

  const onSelect = useCallback(() => {
    setFilters(filtersSelected);
    onClose();
  }, [filtersSelected, onClose, setFilters]);

  const onClear = useCallback(() => {
    setFiltersSelected(defaultFilters);
    setFilters(defaultFilters);
    onClose();
  }, [defaultFilters, onClose, setFilters]);

  useEffect(() => {
    const element = document.getElementById("mobile-filters-body");
    if (element) { 
      element.scrollTo({ top: 0 }); 
    }
  }, []);

  const FiltersHome = useMemo(() => styled.div`
    position: fixed;
    display: flex;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    z-index: 30;
    pointer-events: auto;
    height: 100%;
    width: 100%;
    background: rgba(50,50,50,0.3);
  `, []);

  const Container = useMemo(() => styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    height: 75vh;
    width: 90vw;
    margin-top: 5rem;
    border-radius: 0.75rem;
    background-color: #ffffff;
    pointer-events: auto;
    overflow: hidden;
  `, []);

  const Header = useMemo(() => styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border-bottom: 1px solid rgb(209 213 219);
    pointer-events: auto;
    min-height: 55px;
    width: 100%;
    & > #icon {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
    }
  `, []);

  const Body = useMemo(() => styled.div`
    overflow-y: scroll;
    max-height: auto;
    margin-bottom: auto;
    align-items: center;
  `, []);

  const FilterItem = useMemo(() => styled.div`
    padding: 1rem;
    border-bottom:1 px solid rgb(209 213 219);
    width: 100%;
    & > p {
      margin-top: 0.5rem;
      margin-bottom: 1rem;
    }
  `, []);

  const FilterItemBody = useMemo(() => styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    & > p {
      margin-top: 0.5rem;
      margin-bottom: 1rem;
    }
    & > div {
      display: flex;
      align-items: center;
      gap: 20px;
      width: 50%;
      margin-bottom: 0.75rem;
      & > input{
        height: 30px;
        min-width: 30px;
        border-radius: 1.5rem;
      }
      & > label {
        cursor: pointer;
        width: 100%;
      }
    }`, []);

  // Slider
  const SliderContainer = useMemo(() => styled.div`
    margin: 20px 0;
    width: 100%;
    .rc-slider-track {
      background: ${palette.additional.main};
    }
    .rc-slider-handle {
      border: 2px solid ${palette.additional.main};
      &:hover {
        border-color: blue;
      }
    }
  `, [palette.additional.main]);

  // Buttons
  const Buttons = styled.div`
    width:100%;
    background: ${palette.secondary.light};
    display: flex;
    justify-content: space-between;
    padding: 15px 20px;
    & > button {
      width: 30%;
    }
  `;

  // Price
  const Price = styled.div`
    display: flex;
    gap: 20px;
  `;

  const PriceItem = styled.div`
    width: 100%;
  `;

  const PriceBody = styled.div`
    padding: 5px 10px;
    border-radius: 1.5rem;
    border: 1px solid ${palette.primary.dark};
  `;

  const vehicleTypes = useMemo(() => [
    { value: "Pickup", label: <FormattedMessage {...messages.Pickup} /> },
    { value: "SUV", label: <FormattedMessage {...messages.SUV} /> },
    { value: "Hatchback", label: <FormattedMessage {...messages.Hatchback} /> },
    { value: "Crossover", label: <FormattedMessage {...messages.Crossover} /> },
    { value: "Sedan", label: <FormattedMessage {...messages.Sedan} /> },
    { value: "Sport", label: <FormattedMessage {...messages.Sport} /> },
    { value: "Coupe", label: <FormattedMessage {...messages.Coupe} /> },
    { value: "Cabriolet", label: <FormattedMessage {...messages.Cabriolet} /> },
  ], []);

  const gearboxType = useMemo(() => [
    { value: "Automatic", label: <FormattedMessage {...messages.Automatic} /> },
    { value: "Manual", label: <FormattedMessage {...messages.Manual} /> },
  ], []);

  return (
    <FiltersHome>
      <Container>
        
        <Header>
          <Text id="icon" tag="p" variant="bold" onClick={onClose}>
            <i className="las la-times-circle" role="presentation" />
          </Text>
          <Text tag="span" variant="bold">
            <FormattedMessage {...messages.filters} />
          </Text>
        </Header>

        <Body id="mobile-filters-body">
          <FilterItem>
            <Text tag="p" variant="bold">
              <FormattedMessage {...messages.carTypes} />
            </Text>
            <FilterItemBody id="carTypesFilter">
              {vehicleTypes.map(({ value, label }) => (
                <div key={value}>
                  <input
                    type="checkbox"
                    id={value}
                    checked={filtersSelected?.carTypesFilter?.includes(value.toLowerCase())}
                    onChange={onChange}
                  />
                  <label htmlFor={value}>
                    <Text tag="span" variant="normal">{label}</Text>
                  </label>
                </div>
              ))}
            </FilterItemBody>
          </FilterItem>

          <FilterItem>
            <Text tag="p" variant="bold">
              <FormattedMessage {...messages.fuelTypes} />
            </Text>
            <FilterItemBody id="fuelTypesFilter">
              {["Petrol", "Electric", "Diesel", "Hybrid"].map((item) => (
                <div key={item}>
                  <input
                    type="checkbox"
                    id={item}
                    checked={filtersSelected?.fuelTypesFilter?.includes(item.toLowerCase())}
                    onChange={onChange}
                  />
                  <label htmlFor={item}>
                    <Text tag="span" variant="normal">{item}</Text>
                  </label>
                </div>
              ))}
            </FilterItemBody>
          </FilterItem>

          <FilterItem>
            <Text tag="p" variant="bold">
              <FormattedMessage {...messages.gearbox} />
            </Text>
            <FilterItemBody id="gearboxFilter">
              {gearboxType.map(({ value, label }) => (
                <div key={value}>
                  <input
                    type="checkbox"
                    id={value}
                    checked={filtersSelected?.gearboxFilter?.includes(value.toLowerCase())}
                    onChange={onChange}
                  />
                  <label htmlFor={value}>
                    <Text tag="span" variant="normal">
                      {label}
                    </Text>
                  </label>
                </div>
              ))}
            </FilterItemBody>
          </FilterItem>
          <FilterItem>
            <Text tag="p" variant="normal">
              <FormattedMessage {...messages.price} />
            </Text>
            <SliderContainer>
              <Slider
                range
                min={0}
                max={8000}
                value={filtersSelected.priceRangeFilter ? [Number(filtersSelected?.priceRangeFilter[0]), Number(filtersSelected?.priceRangeFilter[1])] : [0, 500]}
                allowCross={false}
                onChange={(e) => Array.isArray(e) && setFiltersSelected((prev) => ({ ...prev, priceRangeFilter: e.map((item) => String(item)) }))}
              />
            </SliderContainer>
            <Price>
              <PriceItem className="w-full">
                <Text tag="span" variant="normalSmall">
                  <FormattedMessage {...messages.minPrice} />
                </Text>
                <PriceBody>
                  <Text tag="span" variant="boldSmall">
                    {currencySymbol} {filtersSelected.priceRangeFilter && filtersSelected.priceRangeFilter[0]}
                  </Text>
                </PriceBody>
              </PriceItem>
              <PriceItem className="w-full">
                <Text tag="span" variant="normalSmall">
                  <FormattedMessage {...messages.maxPrice} />
                </Text>
                <PriceBody>
                  <Text tag="span" variant="boldSmall">
                    {currencySymbol} {filtersSelected.priceRangeFilter && filtersSelected.priceRangeFilter[1]}
                  </Text>
                </PriceBody>
              </PriceItem>
            </Price>
          </FilterItem>
        </Body>
        <Buttons>
          <Button
            onClick={onClear}
            background="transparent"
            textVariant="normalAdditional"
            id="clear-price-range-button"
          >
            <FormattedMessage {...messages.clear} />
          </Button>
          <Button
            onClick={onSelect}
            textVariant="normalSecondary"
            id="apply-price-range-button"
          >
            <FormattedMessage {...messages.apply} />
          </Button>
        </Buttons>
      </Container>
    </FiltersHome>
  );
}
export default memo(MobileFilters);
